/**
 * AiReviewPage (FND-010, HITL).
 *
 * The human-in-the-loop review queue: low-confidence, advisory AI outputs that
 * a human must confirm (GET /ai/review-tasks). Each open task can be resolved
 * with approve / reject / amend (POST /ai/review-tasks/{id}:resolve). AI is
 * advisory-only — resolving records the decision, it never commits an action.
 * Data wiring (useAiReview) is unchanged.
 */
import { type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useAiReview } from '../hooks/useAiReview';
import type { AIReviewTask, ReviewDecision } from '../api/types';
import { colors } from '../theme';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingIndicator } from '../components/LoadingIndicator';

function pct(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function confidenceColor(c: number): string {
  if (c >= 0.8) return colors.success;
  if (c >= 0.5) return colors.warning;
  return colors.danger;
}

const DECISIONS: ReviewDecision[] = ['approve', 'reject', 'amend'];

export function AiReviewPage() {
  const { tasks, isLoading, isWorking, error, refresh, resolve } = useAiReview();

  const columns: Column<AIReviewTask>[] = [
    { key: 'capability', header: 'Capability', render: (t) => <span style={{ fontWeight: 600 }}>{t.capability}</span> },
    {
      key: 'case',
      header: 'Case',
      render: (t) =>
        t.case_id ? (
          <Link to={`/cases/${t.case_id}`} style={caseLink}>
            {t.case_id}
          </Link>
        ) : (
          <span style={muted}>—</span>
        ),
    },
    {
      key: 'confidence',
      header: 'Confidence',
      align: 'right',
      render: (t) => (
        <span style={{ fontWeight: 700, color: confidenceColor(t.confidence) }}>{pct(t.confidence)}</span>
      ),
    },
    {
      key: 'proposed',
      header: 'Proposed output',
      render: (t) => {
        const proposed = t.proposed_output ?? {};
        const entries = Object.entries(proposed);
        if (entries.length === 0) return <span style={muted}>—</span>;
        return (
          <div style={chipsWrap}>
            {entries.map(([k, v]) => (
              <span key={k} style={chip}>
                {k.replace(/_/g, ' ')}: {String(v)}
              </span>
            ))}
          </div>
        );
      },
    },
    { key: 'status', header: 'Status', render: (t) => <StatusBadge value={t.status} /> },
    {
      key: 'decision',
      header: 'Decision',
      render: (t) =>
        t.status === 'open' ? (
          <div style={actions}>
            {DECISIONS.map((d) => (
              <button
                key={d}
                type="button"
                disabled={isWorking}
                onClick={() => void resolve(t.id, d)}
                className={d === 'approve' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                style={{ textTransform: 'capitalize' }}
              >
                {d}
              </button>
            ))}
          </div>
        ) : (
          <span style={muted}>{t.decision ?? 'resolved'}</span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="AI Review · HITL"
        subtitle="Human-in-the-loop review of low-confidence, advisory AI output (FND-010). Resolving a task records the reviewer's decision; AI never commits an action automatically."
        actions={
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
        }
      />
      <Card title="Review queue" padded={false}>
        {(isLoading || isWorking) && (
          <div style={{ padding: 18 }}>
            <LoadingIndicator text={isWorking ? 'Resolving…' : 'Loading review tasks…'} />
          </div>
        )}
        {error && (
          <p style={errorText} role="alert">
            {error.message}
          </p>
        )}
        {!isLoading && !error && (
          <DataTable
            columns={columns}
            rows={tasks}
            rowKey={(t) => t.id}
            emptyTitle="No HITL review tasks pending"
            emptyDescription="Low-confidence AI suggestions awaiting human confirmation appear here."
          />
        )}
      </Card>
    </>
  );
}

const caseLink: CSSProperties = { color: colors.action, fontWeight: 600 };
const muted: CSSProperties = { color: colors.textSecondary, fontSize: '0.86rem' };
const actions: CSSProperties = { display: 'flex', gap: 6 };
const chipsWrap: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 6 };
const chip: CSSProperties = {
  fontSize: '0.74rem',
  padding: '2px 8px',
  borderRadius: 6,
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  color: colors.textSecondary,
};
const errorText: CSSProperties = { color: colors.danger, fontSize: '0.9rem', padding: 18 };
